import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Monitor, Cpu, MemoryStick } from 'lucide-react';
import api from '../../services/api';
import StatusPill from '../../components/StatusPill';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes > 1024 * 1024 * 1024) return (bytes / 1024 / 1024 / 1024).toFixed(1) + ' GB';
  if (bytes > 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024).toFixed(1) + ' KB';
}

export default function Containers() {
  const [stats, setStats]     = useState([]);
  const [issues, setIssues]   = useState([]);
  const [emailMap, setEmailMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [vncLoading, setVncLoading] = useState({});

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const [s, u] = await Promise.all([
        api.get('/admin/system-stats'),
        api.get('/admin/users', { params: { limit: 200 } }),
      ]);
      setStats(Array.isArray(s.data) ? s.data : []);
      const users = u.data.users || [];
      // Containers are per MT5 ACCOUNT, and getSystemStats labels each one
      // with its account id — so the name map and the issue list are keyed on
      // accounts, with the owner's email carried along for display.
      const map = {};
      const problems = [];
      for (const usr of users) {
        for (const acc of (usr.mt5Accounts || [])) {
          map[acc.id] = `${usr.email} — ${acc.displayLabel}`;
          const unhealthy =
            ['auth_failed', 'error', 'starting', 'awaiting_manual_login', 'manual_login_timeout']
              .includes(acc.containerStatus) ||
            (acc.containerError && acc.containerError.length > 0);
          if (unhealthy) {
            problems.push({
              ...acc,
              _id: acc.id,
              email: usr.email,
              username: usr.username,
              label: acc.displayLabel,
            });
          }
        }
      }
      setEmailMap(map);
      setIssues(problems);
    } finally {
      setLoading(false);
    }
  }

  async function openGui(accountId) {
    if (!accountId) return;
    setVncLoading(p => ({ ...p, [accountId]: true }));
    try {
      const r = await api.get(`/admin/vnc-access/${accountId}`);
      const { vncPassword } = r.data;
      const base = `${window.location.origin}/novnc/${accountId}`;
      const params = new URLSearchParams({
        autoconnect: 'true', resize: 'scale',
        password: vncPassword, path: `novnc/${accountId}/`,
      });
      window.open(`${base}/vnc.html?${params}`, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert(`Cannot open GUI: ${msg}\n\nContainer may need to be restarted.`);
    } finally {
      setVncLoading(p => ({ ...p, [accountId]: false }));
    }
  }

  return (
    <>
      <div className="ct-page-header">
        <div>
          <h1>Containers</h1>
          <div className="subtitle">Live MT5 instance health and noVNC access</div>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={load}
          style={{display:'inline-flex',alignItems:'center',gap:6}}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {issues.length > 0 && (
        <div className="card" style={{borderLeft:`3px solid var(--loss)`}}>
          <div className="card-header" style={{display:'flex',alignItems:'center',gap:8}}>
            <AlertTriangle size={16} color="var(--loss)" />
            <h5 className="card-title" style={{color:'var(--loss)'}}>Issues ({issues.length})</h5>
          </div>
          <div className="card-body" style={{padding:0}}>
            <div className="table-responsive table-cards table-cards-wrap">
              <table className="table">
                <thead>
                  <tr><th>Account</th><th>Status</th><th>Error</th><th>When</th></tr>
                </thead>
                <tbody>
                  {issues.map(u => (
                    <tr key={u._id}>
                      <td data-label="Account" className="small">
                        <div>{u.email}</div>
                        <div style={{color:'var(--text-tertiary)'}}>{u.label}</div>
                      </td>
                      <td data-label="Status"><StatusPill status={u.containerStatus} /></td>
                      <td data-label="Error" className="small" style={{maxWidth:420, color:'var(--loss)', wordBreak:'break-word'}}>
                        {u.containerError || '—'}
                      </td>
                      <td data-label="When" className="small" style={{color:'var(--text-tertiary)'}}>
                        {u.containerErrorAt ? new Date(u.containerErrorAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h5 className="card-title">Container Health Monitor</h5>
          <div className="card-category" style={{marginTop:4}}>{stats.length} running</div>
        </div>
        <div className="card-body" style={{padding:0}}>
          {loading ? <div style={{padding:20, color:'var(--text-secondary)'}}>Loading container stats…</div> : (
            stats.length === 0 ? (
              <div style={{padding:24, color:'var(--text-secondary)', textAlign:'center'}}>
                No MT5 containers running.
              </div>
            ) : (
              <div className="table-responsive table-cards table-cards-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Container</th>
                      <th>Account</th>
                      <th>Status</th>
                      <th><span style={{display:'inline-flex',alignItems:'center',gap:6}}><Cpu size={12} /> CPU</span></th>
                      <th><span style={{display:'inline-flex',alignItems:'center',gap:6}}><MemoryStick size={12} /> Memory</span></th>
                      <th>Limit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map(c => (
                      <tr key={c.containerId}>
                        <td data-label="Container" className="small" style={{fontFamily:'JetBrains Mono, ui-monospace, monospace', color:'var(--text-secondary)'}}>{c.name}</td>
                        <td data-label="Account" className="small">
                          {emailMap[c.accountId || c.userId]
                            || <span style={{color:'var(--text-tertiary)'}}>{c.accountId || c.userId || '—'}</span>}
                        </td>
                        <td data-label="Status"><StatusPill status={c.status} /></td>
                        <td data-label="CPU" className="num">{c.cpu || '—'}</td>
                        <td data-label="Memory" className="num">{formatBytes(c.memUsage)}</td>
                        <td data-label="Limit" className="num" style={{color:'var(--text-tertiary)'}}>{formatBytes(c.memLimit)}</td>
                        <td data-label="Actions">
                          {(c.accountId || c.userId) && c.status === 'running' ? (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openGui(c.accountId || c.userId)}
                              disabled={!!vncLoading[c.accountId || c.userId]}
                              title="Open MT5 GUI in browser via noVNC"
                              style={{display:'inline-flex',alignItems:'center',gap:6}}
                            >
                              <Monitor size={13} />
                              {vncLoading[c.accountId || c.userId] ? 'Opening…' : 'Open VNC'}
                            </button>
                          ) : (
                            <span style={{color:'var(--text-tertiary)', fontSize:12}}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}
